import { createContext, useContext, useEffect, useState } from 'react';
import { Auth, AuthState, User } from '@opoint/authjs';

/**
 * Context that holds a global Auth object.
 */
export const OpointAuthContext = createContext<Auth | undefined>(undefined);

/**
 * Provides the auth state if the user is signed in. Must either be inside a OpointAuthContext.Provider, or receive the `auth` param.
 * @param authParam an Auth object. Only one such object should exist in your application. Only pass this parameter if not using a OpointAuthContext.Provider.
 * @returns AuthState if signed in, null if not, and undefined when waiting for the state to resolve (e.g page refreshed).
 */
export function useOpointAuthState(authParam?: Auth): AuthState | null | undefined {
	const authContext = useContext(OpointAuthContext);
	const auth = authContext ?? authParam;
	const [, setState] = useState<{}>({});

	async function updateToken() {
		await auth?.getState();
		setState({});
	}

	if (!auth) return null;

	useEffect(() => {
		updateToken();
	}, [auth, auth?.state]);

	if (auth.state && auth.state.token && auth.state.token.expiry < new Date()) {
		updateToken();
		return undefined;
	}

	return auth.state;
}

/**
 * Provides the OpointUser if the user is signed in. Must either be inside a OpointAuthContext.Provider, or receive the `auth` param.
 * @param auth an Auth object. Only one such object should exist in your application. Only pass this parameter if not using a OpointAuthContext.Provider.
 * @returns OpointUser if signed in, null if not, and undefined when waiting for the state to resolve (e.g page refreshed).
 */
export function useOpointUser(auth?: Auth): User | null | undefined {
	const authState = useOpointAuthState(auth);
	if (authState === null || authState === undefined) return authState;
	return authState.user;
}

/**
 * Creates a Auth object for the global auth state. Only use this hook once, high up in your tree.
 * @param API_URL - The hostname of the API you want to use. This must be same-site with the current origin (i.e cookes considered first-party).
 * @param callback - The callback URL after a sucessful login.
 * @param autologin - If true, the user will automatically be redirected if the login state resolves as unauthenticated.
 * @param persistentImpersonation - If true, will store the impersonation state to persist across e.g a page refresh.
 * @returns
 */
export function useOpointAuth(
	API_URL: string,
	callback: string,
	autologin: boolean = true,
	persistentImpersonation: boolean = false
): Auth {
	const [, setState] = useState({});
	class ReactAuth extends Auth {
		async logout(): Promise<undefined> {
			const result = await super.logout();
			setState({});
			return result;
		}
	}
	const [auth] = useState(new ReactAuth(API_URL, callback, autologin, persistentImpersonation));
	return auth;
}
